import { useMemo, useState, useEffect, ChangeEvent, useRef } from 'react';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Menu,
    Flex,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { ColumnDef, SortingFn, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { TClientCategoryItem, TGlobalCategory, TOptionalGlobalCategory } from 'types';
import {
    useDeleteGlobalCategoryMutation,
    useGetClientCategoriesQuery,
    useGetGlobalCategoriesQuery,
    useLazyGetClientCategoryByIdQuery,
    useLazyExportCategoriesQuery
} from 'core/API/globalCategoriesApi';
import { ChevronDownIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { linkStyle } from 'shared/styles';
import { EXCEL_FILE_TYPE } from 'shared/const';
import FileSaver from 'file-saver';

const useGlobalCategories = () => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const translate = useTranslation();
    const [dataToSubmit, setDataToSubmit] = useState<TOptionalGlobalCategory>({ client_categories: [] });
    const {
        data: globalCategoriesData,
        isLoading: isGlobalCategoriesLoading,
        isFetching: isGlobalCategoriesFetching,
        isError: isGlobalCategoriesError,
    } = useGetGlobalCategoriesQuery();
    const [getClientCategoryById] = useLazyGetClientCategoryByIdQuery();
    const {
        data: clientCategories,
        isLoading: isClientCategoriesLoading,
        isFetching: isClientCategoriesFetching,
        isError: isClientCategoriesError,
    } = useGetClientCategoriesQuery({ hasParent: false });
    const [availableCategories, setAvailableCategories] = useState<TClientCategoryItem[]>([]);
    const [exportCategories, { isLoading: isExportLoading, isFetching: isExportFetching }] =
        useLazyExportCategoriesQuery();
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const companyInfo = useGetCompanyInfoSelector();
    const [isDeleteMultiple, setIsDeleteMultiple] = useState(false);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const deleteInitialRef = useRef(null);
    const toast = useToast();
    const [deleteGlobalCategory, { isLoading: isDeleteLoading }] = useDeleteGlobalCategoryMutation();
    const userInfo = useGetUserInfoSelector();
    const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    useEffect(() => {
        if (!isClientCategoriesLoading && !isClientCategoriesError && clientCategories) {
            setAvailableCategories(clientCategories);
        }
    }, [clientCategories, isClientCategoriesLoading, isClientCategoriesError]);

    const data: Readonly<TGlobalCategory[]> = useMemo(() => {
        if (!isGlobalCategoriesLoading && !isGlobalCategoriesError && globalCategoriesData) {
            return globalCategoriesData.global_categories;
        }
        return [];
    }, [isGlobalCategoriesLoading, isGlobalCategoriesError, globalCategoriesData]);

    const caseInsensitiveSort = useMemo(() => SORTING_FNS.caseInsensitive, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const handleCloseDelete = () => {
        onDeleteClose();
    };

    const handleDelete = (multiple: boolean) => {
        setIsDeleteMultiple(multiple);
        onDeleteOpen();
    };

    const handleExportCategories = async () => {
        try {
            const { data: res, isError: isExportError } = await exportCategories();
            if (isExportError || !res) throw new Error(translate('categories.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });
            FileSaver.saveAs(blob, translate('categories.export.file_name') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleOpenModal = (type: 'add' | 'update') => {
        setModalType(type);
        setIsModalOpen(true);
    };

    const handleDeleteGlobalCategory = async () => {
        try {
            const deleteBody = isDeleteMultiple ? selectedCategories : [categoryToDelete];
            await deleteGlobalCategory(deleteBody as number[]).unwrap();
            if (isDeleteMultiple) setSelectedCategories([]);
            onDeleteClose();
            toast({
                title: translate('categories.modal.deleteSuccesful'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: number) => {
        if (event.target.checked) setSelectedCategories([...selectedCategories, id]);
        else setSelectedCategories(selectedCategories.filter((e) => e !== id));
    };

    const handleCheckAllRows = () => {
        if (globalCategoriesData) {
            if (selectedCategories.length < globalCategoriesData.global_categories.length)
                setSelectedCategories(globalCategoriesData.global_categories.map((gc) => gc.id));
            else setSelectedCategories([]);
        }
    };

    const columns: Readonly<ColumnDef<TGlobalCategory, any>[]> = useMemo(() => {
        const nbCategories = globalCategoriesData ? globalCategoriesData.global_categories.length : 1;
        const tableColumns: readonly (ColumnDef<TGlobalCategory, any> | null)[] = [
            {
                header: () => (
                    <Center m={0}>
                        <Checkbox
                            onChange={handleCheckAllRows}
                            isIndeterminate={selectedCategories.length > 0 && selectedCategories.length < nbCategories}
                            isChecked={selectedCategories.length === nbCategories}
                        ></Checkbox>
                    </Center>
                ),
                id: 'selector',
                enableColumnFilter: false,
                cell: (info) => {
                    const cellValue = info.row.original as TGlobalCategory;
                    return (
                        <Box>
                            <Checkbox
                                onChange={(e) => {
                                    handleCheckRow(e, cellValue.id);
                                }}
                                isChecked={selectedCategories.includes(cellValue.id)}
                            ></Checkbox>
                        </Box>
                    );
                },
                meta: {
                    width: '2%',
                },
            } as ColumnDef<TGlobalCategory, any>,

            {
                header: () => translate('global_categories.column.category') as string,
                accessorKey: 'level_name_1',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TGlobalCategory;
                    return (
                        <Box>
                            <Text>{cellValue.level_name_1}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TGlobalCategory, any>,

            {
                header: () => translate('global_categories.column.subcategory') as string,
                accessorKey: 'level_name_2',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TGlobalCategory;
                    return (
                        <Box>
                            <Text>{cellValue.level_name_2 || '-'}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TGlobalCategory, any>,

            {
                header: () => translate('global_categories.column.segment') as string,
                accessorKey: 'level_name_3',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TGlobalCategory;
                    return (
                        <Box>
                            <Text>{cellValue.level_name_3 || '-'}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TGlobalCategory, any>,
            {
                header: () => translate(`list.column.actions`) as string,
                accessorKey: 'actions',
                meta: {
                    width: '10%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TGlobalCategory;
                    return (
                        <Flex>
                            <Menu size="xs">
                                <MenuButton size="xs" as={Button} rightIcon={<ChevronDownIcon />}>
                                    {translate(`list.menu.button.actions`)}
                                </MenuButton>
                                <MenuList>
                                    <MenuItem
                                        onClick={async () => {
                                            setCategoryToDelete(cellValue.id);
                                            handleDelete(false);
                                        }}
                                        icon={<DeleteIcon />}
                                    >
                                        {translate(`global_categories.menu.button.delete`)}
                                    </MenuItem>

                                    <MenuItem
                                        onClick={async () => {
                                            const globalCategoryById = await getClientCategoryById(
                                                cellValue.id as number,
                                            );
                                            const currentCategory = globalCategoryById?.data as TGlobalCategory;
                                            if (clientCategories) {
                                                setAvailableCategories(clientCategories);
                                            }
                                            setDataToSubmit({
                                                ...cellValue,
                                                client_categories: currentCategory
                                                    ? currentCategory.client_categories
                                                    : [],
                                            });
                                            handleOpenModal('update');
                                        }}
                                        icon={<EditIcon />}
                                    >
                                        {translate(`global_categories.menu.button.edit`)}
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        </Flex>
                    );
                },
            } as ColumnDef<TGlobalCategory, any>,
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<TGlobalCategory, any> => e !== null);
        return filteredColumns;
    }, [globalCategoriesData, selectedCategories, languages, userInfo, clientCategories]);

    const table = useReactTable<TGlobalCategory>({
        columns: columns as ColumnDef<TGlobalCategory, any>[],
        data: data as TGlobalCategory[],
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters);
    }, [table.getState().columnFilters]);

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            dataToSubmit,
            columns,
            table,
            globalCategoriesData,
            isGlobalCategoriesLoading,
            isGlobalCategoriesFetching,
            isGlobalCategoriesError,
            availableCategories,
            setAvailableCategories,
            companyInfo,
            userInfo,
            defaultColumn,
            clientCategories,
            data,
            selectedCategories,
            isDeleteOpen,
            handleCloseDelete,
            isDeleteLoading,
            handleDeleteGlobalCategory,
            isDeleteMultiple,
            deleteInitialRef,
            setSelectedCategories,
            sortTypes,
            modalType,
            exportCategories,
            isExportLoading,
            isExportFetching,
            handleExportCategories,
            isModalOpen,
            deleteGlobalCategory,
            setDataToSubmit,
            setLanguages,
            handleDelete,
            isClientCategoriesLoading,
            isClientCategoriesFetching,
            caseInsensitiveSort,
            handleOpenModal,
            setModalType,
            setIsModalOpen,
        }),
        [
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            dataToSubmit,
            clientCategories,
            columns,
            table,
            globalCategoriesData,
            isGlobalCategoriesFetching,
            availableCategories,
            isGlobalCategoriesLoading,
            isClientCategoriesLoading,
            isClientCategoriesFetching,
            isGlobalCategoriesError,
            isExportLoading,
            isExportFetching,
            isDeleteLoading,
            isDeleteOpen,
            deleteInitialRef,
            isDeleteMultiple,
            selectedCategories,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            sortTypes,
            modalType,
            isModalOpen,
        ],
    );

    return props;
};

export type TGlobalCategoriesHooks = ReturnType<typeof useGlobalCategories>;

export default useGlobalCategories;
